import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
  useParams,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";

import Shell from "@/components/shell";
import ErrorFallback from "@/components/ui/errorFallback";
import SuspenseWrapper from "@/components/ui/suspenseWrapper";
import {
  LEGACY_ARCHIVE_ROUTE,
  LEGACY_ENTRY_ROUTE,
  LEGACY_REFERRER_ROUTE,
  LOCALE_PARAM,
  ROUTES,
} from "@/constants/routes";
import {
  ArchivePageLazy,
  ImportCsvPageLazy,
  BulkUrlShortenerPageLazy,
  DomainPageLazy,
  EntryPageLazy,
  FaqPageLazy,
  IntegrationPageLazy,
  ProfilePageLazy,
  ReferrerPageLazy,
  StatisticsPageLazy,
  SubscriptionPageLazy,
  SupportPageLazy,
  TestPageLazy,
  ChangeOwnerPageLazy,
  SettingsPageLazy,
  ShareFolderAccessLazy,
} from "@/pages";
import { getPagePathSegment } from "@/utils/getPagePathSegment";

const ENTRY_SEGMENT = getPagePathSegment(ROUTES.ENTRY);

const CatchAllRedirect = () => {
  const { locale } = useParams();
  const targetPath = locale
    ? `/${locale}/${ENTRY_SEGMENT}`
    : `/${ENTRY_SEGMENT}`;

  return <Navigate to={targetPath} replace />;
};

const LegacyRedirect = ({ to }: { to: string }) => {
  const { locale } = useParams();
  const { search, hash } = useLocation();
  const pathname = locale ? `/${locale}/${to}` : `/${to}`;

  return <Navigate to={{ pathname, search, hash }} replace />;
};

const legacyRoutes = [
  {
    path: getPagePathSegment(LEGACY_ENTRY_ROUTE),
    element: <LegacyRedirect to={ENTRY_SEGMENT} />,
  },
  {
    path: getPagePathSegment(LEGACY_ARCHIVE_ROUTE),
    element: <LegacyRedirect to={getPagePathSegment(ROUTES.ARCHIVE)} />,
  },
  {
    path: getPagePathSegment(LEGACY_REFERRER_ROUTE),
    element: <LegacyRedirect to={getPagePathSegment(ROUTES.REFERRER)} />,
  },
];

const routes = (locale: string | undefined = "") => {
  const basePrefix = locale ? `/${locale}` : "";
  const defaultRedirectPath = ENTRY_SEGMENT;

  return [
    {
      path: basePrefix || "/",
      element: <Shell />,
      errorElement: <ErrorFallback />,
      children: [
        { index: true, element: <Navigate to={defaultRedirectPath} replace /> },
        ...legacyRoutes,
        {
          element: <SuspenseWrapper />,
          children: [
            {
              path: getPagePathSegment(ROUTES.ENTRY),
              element: <EntryPageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.ARCHIVE),
              element: <ArchivePageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.ENTRY_CHANGE_OWNER),
              element: <ChangeOwnerPageLazy mode="qr" />,
            },
            {
              path: getPagePathSegment(ROUTES.FOLDER_CHANGE_OWNER),
              element: <ChangeOwnerPageLazy mode="folder" />,
            },
            {
              path: getPagePathSegment(ROUTES.SHARE_FOLDER_ACCESS),
              element: <ShareFolderAccessLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.CSV_IMPORT),
              element: <ImportCsvPageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.BULK_URL_SHORTENER),
              element: <BulkUrlShortenerPageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.DOMAIN),
              element: <DomainPageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.FAQ),
              element: <FaqPageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.INTEGRATION),
              element: <IntegrationPageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.PROFILE),
              element: <ProfilePageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.REFERRER),
              element: <ReferrerPageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.STATISTICS),
              element: <StatisticsPageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.STATISTICS_ID),
              element: <StatisticsPageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.SUBSCRIPTION),
              element: <SubscriptionPageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.SUPPORT),
              element: <SupportPageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.ENTRY_SETTINGS),
              element: <SettingsPageLazy />,
            },
            {
              path: getPagePathSegment(ROUTES.FOLDER_SETTINGS),
              element: <SettingsPageLazy mode="folder" />,
            },
            {
              path: getPagePathSegment(ROUTES.TEST),
              element: <TestPageLazy />,
            },
          ],
        },
        {
          path: "*",
          element: <CatchAllRedirect />,
        },
      ],
    },
  ];
};

const router = createBrowserRouter([
  ...routes(),
  ...routes(`:${LOCALE_PARAM}`),
]);

function App() {
  return (
    <>
      <RouterProvider router={router} />
      <ToastContainer limit={5} />
    </>
  );
}

export default App;
